package top.wcpe.allincore.ui.runtime.ir.call

import top.wcpe.allincore.dsl.ir.expr.ExprNode
import top.wcpe.allincore.dsl.ir.expr.StmtNode
import top.wcpe.allincore.dsl.ir.model.action.IrFun
import top.wcpe.allincore.ui.runtime.ir.DslRuntimeError
import top.wcpe.allincore.ui.runtime.ir.DslValueUtils
import top.wcpe.allincore.ui.runtime.ir.api.UiCommandApi
import top.wcpe.allincore.ui.runtime.ir.eval.EvalContext
import top.wcpe.allincore.ui.runtime.ir.eval.ExprEvaluator
import top.wcpe.allincore.ui.runtime.ir.state.StateMachineExecutor

/**
 * 可调用对象调用 + CallExpr 分派（拆分自 [CallDispatcher]）。
 *
 * 集中处理：
 * - [ExprNode.CallExpr]：按名解析（局部 fun → 局部 var → screen 级 fun → 函数引用），
 *   或对求值后的运行时值按类型分派
 * - [LambdaValue] / [LocalFunValue] / [IrFun]（screen 级 fun）的调用语义
 * - [bindArgs]：位置 + 命名实参对齐与默认值求值
 *
 * 依赖 DAG：处于 [CallArgs] 之上；被 [BuiltinCallEvaluator] 与 [MemberCallDispatcher]
 * 复用（builtin 回调 / animate onComplete / net.subscribe handler / timer 回调）。
 */
@Suppress("TooManyFunctions")
internal object CallableInvoker {

    // SPLICE_LAMBDA

    /**
     * 构造 LambdaValue。
     *
     * M5-a 闭包语义分流：
     * - 普通 lambda：按引用闭包（不捕获），调用时沿 ctx 栈往外找。
     * - suspend lambda：仍按值捕获（异步执行时外层帧可能已 pop）。
     *
     * M5-e 备注：suspend lambda 因异步挂起时外层栈帧可能已 pop，
     * 即便 M5-a 起普通 lambda 改为按引用闭包，suspend lambda 仍保留按值
     * snapshot 语义。该锁定行为由 SuspendLambdaCapturesByValueTest 守护：
     * 创建 suspend lambda 后，若外层在调用前修改了被捕获的 var，
     * suspend lambda 内读到的仍为创建时的快照值。
     */
    @Suppress("UNCHECKED_CAST")
    fun makeLambdaValue(lam: ExprNode.Lambda, ctx: EvalContext): LambdaValue {
        // snapshotVars() 返回 Map<String, Any?>；LambdaValue.captured 声明为 Map<String, Any>，
        // 与原 Java（按擦除存入）行为一致——快照内的 null 值仍如实保留并向下流转，故此处仅做未检查转型。
        val captured: Map<String, Any> =
            if (lam.isSuspend) ctx.snapshotVars() as Map<String, Any> else emptyMap()
        return LambdaValue(lam.params, lam.body, captured, lam.isSuspend, lam.defaults)
    }

    /**
     * 调用 lambda：push 独立 var 帧（含 captured + 参数），执行 body，最后帧 pop。
     *
     * 关于 await：lambda body 内的 await（顶层裸 / var = 形态）会被 ExprSerializer
     * 编译为 STATE_MACHINE_HEADER stmt；evalBranchBody 执行到该 stmt 时 lambda
     * 的同步部分立即返回 null，状态机异步推进 —— 这等价于"lambda 提前完成返回 null"。
     * 因此 lambda body 末位若为表达式位置 await（其值会成为 lambda 返回值）由 D2-c
     * AwaitPositionChecker 在编译期报 DSL_E605 拦下，本方法不需处理此场景。
     */
    fun invokeLambda(lam: LambdaValue, argValues: List<Any?>, ctx: EvalContext): Any? =
        invokeLambda(lam, argValues, emptyMap(), ctx)

    /** M5-b：带命名实参的 lambda 调用。 */
    fun invokeLambda(lam: LambdaValue, argValues: List<Any?>, namedValues: Map<String, Any?>, ctx: EvalContext): Any? {
        // M4 A2 Stage 3：suspend lambda 必须通过 await 调用。runtime 兜底，应对编译期
        // SuspendLambdaUsageChecker 漏网之鱼（如把 suspend lambda 通过参数 / 字段
        // 间接传递再同步调用）。await 路径走 StateMachineExecutor.invokeSuspendLambda，
        // 不经过本方法。
        if (lam.isSuspend) {
            throw DslRuntimeError("suspend lambda 必须使用 await 调用")
        }
        ctx.pushVarFrame()
        try {
            bindArgs("lambda", lam.params, lam.defaults, argValues, namedValues, ctx)
            return ExprEvaluator.evalBranchBody(lam.body, ctx)
        } catch (rs: ExprEvaluator.ReturnSignal) {
            return rs.value
        } finally {
            ctx.popVarFrame()
        }
    }

    // SPLICE_CALLEXPR

    /** 通用调用表达式：先尝试按名解析（局部 fun → 局部 var → screen 级 fun），再按值分派。 */
    @Suppress("ReturnCount")
    fun evalCallExpr(call: ExprNode.CallExpr, ctx: EvalContext): Any? {
        // 预先求值参数（按从左到右顺序，匹配通用调用约定）
        val argValues = ArrayList<Any?>(call.args.size)
        for (arg in call.args) {
            argValues.add(ExprEvaluator.eval(arg, ctx))
        }
        // M5-b：求值命名实参；保持出现顺序（LinkedHashMap）。
        val namedValues: Map<String, Any?>
        val rawNamed = call.namedArgs
        if (rawNamed.isEmpty()) {
            namedValues = emptyMap()
        } else {
            val nv = LinkedHashMap<String, Any?>(rawNamed.size)
            for (na in rawNamed) {
                if (nv.containsKey(na.name)) {
                    throw DslRuntimeError("命名实参重复: ${na.name}")
                }
                nv[na.name] = ExprEvaluator.eval(na.value, ctx)
            }
            namedValues = nv
        }
        val callee = call.callee
        if (callee is ExprNode.LocalRef) {
            return dispatchByLocalRef(callee.name, argValues, namedValues, ctx)
        }
        if (callee is ExprNode.IdentRef) {
            return dispatchByIdentRef(callee, argValues, namedValues, ctx)
        }
        if (callee is ExprNode.FunRef) {
            val ref = callee
            // M6-e：CallExpr(callee=FunRef(bound=...), args) 形态——把 bound snapshot 与
            // 本次实参合并后再分派。正常 parser 路径下 boundArgs 为空，这里仅作健壮兜底。
            if (!ref.boundArgs.isEmpty() || !ref.boundNamedArgs.isEmpty()) {
                val bound = ExprEvaluator.eval(ref, ctx)
                return invokeCallable(bound, argValues, namedValues, ctx, ref.name)
            }
            return dispatchByFunRef(ref.name, argValues, namedValues, ctx)
        }
        // 其它形式（lambda 字面量、成员访问结果等）：求值后按值分派
        val value = ExprEvaluator.eval(callee, ctx)
        return invokeCallable(value, argValues, namedValues, ctx, "<expr>")
    }

    /** LocalRef 分派：局部 fun → 局部 var 持有的可调用值 → screen 级 fun。 */
    private fun dispatchByLocalRef(
        name: String,
        argValues: List<Any?>,
        namedValues: Map<String, Any?>,
        ctx: EvalContext,
    ): Any? {
        val lf = ctx.findLocalFun(name)
        if (lf != null) return invokeLocalFun(lf, argValues, namedValues, ctx)
        if (ctx.hasVar(name)) {
            val v = ctx.getVar(name)
            return invokeCallable(v, argValues, namedValues, ctx, name)
        }
        val fn = ctx.screenFunctions[name]
        if (fn != null) return invokeScreenFun(fn, argValues, namedValues, ctx)
        throw DslRuntimeError("未声明的可调用名: $name")
    }

    /** IdentRef 分派：局部 fun → screen 级 fun → 求值后按值分派（兜底回调参数等）。 */
    private fun dispatchByIdentRef(
        ident: ExprNode.IdentRef,
        argValues: List<Any?>,
        namedValues: Map<String, Any?>,
        ctx: EvalContext,
    ): Any? {
        val name = ident.name
        val lf = ctx.findLocalFun(name)
        if (lf != null) return invokeLocalFun(lf, argValues, namedValues, ctx)
        val fn = ctx.screenFunctions[name]
        if (fn != null) return invokeScreenFun(fn, argValues, namedValues, ctx)
        val v = ExprEvaluator.eval(ident, ctx)
        return invokeCallable(v, argValues, namedValues, ctx, name)
    }

    /** FunRef 分派：screen 级 fun → 局部 fun（兜底）。 */
    private fun dispatchByFunRef(
        name: String,
        argValues: List<Any?>,
        namedValues: Map<String, Any?>,
        ctx: EvalContext,
    ): Any? {
        val fn = ctx.screenFunctions[name]
        if (fn != null) return invokeScreenFun(fn, argValues, namedValues, ctx)
        val lf = ctx.findLocalFun(name)
        if (lf != null) return invokeLocalFun(lf, argValues, namedValues, ctx)
        throw DslRuntimeError("未声明的函数: ::$name")
    }

    // SPLICE_INVOKE_CALLABLE

    /** 把任意运行时值当作可调用对象分派；不可调用即抛错。 */
    fun invokeCallable(callee: Any?, argValues: List<Any?>, ctx: EvalContext, hint: String): Any? =
        invokeCallable(callee, argValues, emptyMap(), ctx, hint)

    /** M5-b：带命名实参的可调用对象分派。 */
    @Suppress("ReturnCount", "ThrowsCount")
    fun invokeCallable(
        callee: Any?,
        argValues: List<Any?>,
        namedValues: Map<String, Any?>,
        ctx: EvalContext,
        hint: String,
    ): Any? {
        if (callee is LambdaValue) {
            return invokeLambda(callee, argValues, namedValues, ctx)
        }
        if (callee is LocalFunValue) {
            return invokeLocalFun(callee, argValues, namedValues, ctx)
        }
        if (callee is FunRefValue) {
            val name = callee.name
            val fn = ctx.screenFunctions[name]
            if (fn != null) return invokeScreenFun(fn, argValues, namedValues, ctx)
            val lf = ctx.findLocalFun(name)
            if (lf != null) return invokeLocalFun(lf, argValues, namedValues, ctx)
            throw DslRuntimeError("函数引用 ::$name 未指向已声明的 fun")
        }
        if (callee is BoundFunRefValue) {
            // M6-e：合并 snapshot 实参 + 调用时实参，转发给 dispatchByFunRef。
            val b = callee
            val mergedArgs = ArrayList<Any?>(b.boundArgs.size + argValues.size)
            mergedArgs.addAll(b.boundArgs)
            mergedArgs.addAll(argValues)
            val mergedNamed: Map<String, Any?>
            if (b.boundNamedArgs.isEmpty()) {
                mergedNamed = namedValues
            } else {
                val mn = LinkedHashMap<String, Any?>(b.boundNamedArgs)
                for (e in namedValues.entries) {
                    if (mn.containsKey(e.key)) {
                        throw DslRuntimeError("命名实参重复（bound 与调用时同名）: ${e.key}")
                    }
                    mn[e.key] = e.value
                }
                mergedNamed = mn
            }
            return dispatchByFunRef(b.name, mergedArgs, mergedNamed, ctx)
        }
        // 特性②:ui("id") —— ui 求值为 UiCommandApi,以 1 个字符串实参调用 → 解析元素句柄。
        if (callee is UiCommandApi) {
            if (argValues.size != 1 || argValues[0] !is String) {
                throw DslRuntimeError("ui(id) 需要 1 个字符串参数,实际 ${argValues.size}")
            }
            return callee.find(argValues[0] as String)
        }
        throw DslRuntimeError("不可调用的目标: $hint (${DslValueUtils.classOf(callee)})")
    }

    // SPLICE_INVOKE_LOCAL

    /**
     * 调用局部 fun（按引用闭包）：在当前 ctx 上 push 一帧作为参数帧，执行 body 后 pop。
     * 由于不创建新 EvalContext，body 内对外层 var 的查找/赋值穿透写回原栈。
     */
    private fun invokeLocalFun(lf: LocalFunValue, argValues: List<Any?>, ctx: EvalContext): Any? =
        invokeLocalFun(lf, argValues, emptyMap(), ctx)

    private fun invokeLocalFun(
        lf: LocalFunValue,
        argValues: List<Any?>,
        namedValues: Map<String, Any?>,
        ctx: EvalContext,
    ): Any? {
        ctx.pushVarFrame()
        try {
            bindArgs(
                "fun '${lf.name}'",
                lf.params,
                lf.defaults,
                argValues,
                namedValues,
                ctx,
            )
            return invokeBodyWithParamAwareness(lf.body, lf.params, ctx)
        } catch (rs: ExprEvaluator.ReturnSignal) {
            return rs.value
        } finally {
            ctx.popVarFrame()
        }
    }

    // SPLICE_INVOKE_SCREEN

    /**
     * 调用 screen 级 fun：派生干净上下文，仅注入实参，执行解码后的 body。
     * 与 Kotlin 顶层 fun 语义一致——不捕获外层局部 var。
     */
    private fun invokeScreenFun(fn: IrFun, argValues: List<Any?>, ctx: EvalContext): Any? =
        invokeScreenFun(fn, argValues, emptyMap(), ctx)

    private fun invokeScreenFun(
        fn: IrFun,
        argValues: List<Any?>,
        namedValues: Map<String, Any?>,
        ctx: EvalContext,
    ): Any? {
        val fresh = ctx.freshForScreenFun()
        // M5-b：把默认值 expr blob 解码为 ExprNode 列表，复用 bindArgs 对齐算法。
        // 默认值在 fresh 上下文中求值（与 Kotlin 顶层 fun 行为一致——不可见外层局部 var）。
        val params = fn.params
        val defaults: List<ExprNode?>
        if (fn.defaultBlobs.isEmpty()) {
            defaults = emptyList()
        } else {
            val d = ArrayList<ExprNode?>(params.size)
            for (i in params.indices) {
                d.add(fn.decodeDefault(i))
            }
            defaults = d
        }
        // freshForScreenFun 已经包含底帧，此处不再 pushVarFrame，直接在底帧 declareVar 即可。
        bindArgs("fun '${fn.name}'", params, defaults, argValues, namedValues, fresh)
        try {
            return invokeBodyWithParamAwareness(fn.decodeBody(), params, fresh)
        } catch (rs: ExprEvaluator.ReturnSignal) {
            return rs.value
        }
    }

    /**
     * 修复 fun 形参跨 await 丢失：当 body 被 lower 为单条 [StmtNode.StateMachine] 时，
     * 把形参从当前栈顶帧抽出，走 [StateMachineExecutor.executeStateMachineWithParams]
     * 入口扩展 record schema 并预填值，使形参成为"跨 await 持久化"的槽位；
     * 否则按原路径执行。
     *
     * 调用约束：调用方在调用本方法前必须已经把所有 [params] 通过 [bindArgs] 写入
     * [ctx] 的当前栈顶帧（`_` 丢弃形参例外，未写入）。
     */
    @Suppress("UNCHECKED_CAST")
    private fun invokeBodyWithParamAwareness(body: List<StmtNode>, params: List<String>, ctx: EvalContext): Any? {
        if (body.size == 1 && body[0] is StmtNode.StateMachine) {
            // 从栈顶帧取出形参当前值（含默认值求值结果），传给状态机入口预填到 record。
            // _ 丢弃形参未写入帧，用 null 占位以保持 paramNames / paramValues 长度对齐。
            val paramValues = ArrayList<Any?>(params.size)
            for (p in params) {
                paramValues.add(if ("_" == p) null else ctx.getVar(p))
            }
            // executeStateMachineWithParams 形参为 List<Any>，但与原 Java 一致此处允许 null 占位，
            // 故按未检查转型传入（运行期 record 接受 Any? 槽位）。
            StateMachineExecutor.executeStateMachineWithParams(
                body[0] as StmtNode.StateMachine,
                ctx,
                params,
                paramValues as List<Any>,
            )
            return null
        }
        return ExprEvaluator.evalBranchBody(body, ctx)
    }

    // SPLICE_BIND_ARGS

    /**
     * M5-b：把位置实参 + 命名实参对齐到形参列表，并写入当前栈顶 var 帧。
     *
     * 对齐顺序：
     * 1. 位置实参按出现顺序绑定到前 N 个形参；多余位置实参立即报错。
     * 2. 命名实参按名绑定到剩余形参；未知名 / 与位置实参冲突立即报错。
     * 3. 仍未绑定的形参取默认值（在当前 ctx 中求值）。
     * 4. 仍未绑定 → 抛 DslRuntimeError（对应编译期 E615）。
     *
     * 形参名为 `"_"` 时不写入 var 帧（M5-a 丢弃形参约定）。
     */
    @Suppress("CyclomaticComplexMethod", "NestedBlockDepth", "ThrowsCount")
    private fun bindArgs(
        hint: String,
        params: List<String>,
        defaults: List<ExprNode?>,
        argValues: List<Any?>,
        namedValues: Map<String, Any?>,
        ctx: EvalContext,
    ) {
        val paramCount = params.size
        val posCount = argValues.size
        if (posCount > paramCount) {
            throw DslRuntimeError("$hint 位置实参过多：期望最多 $paramCount，实际 $posCount")
        }
        // 标记已绑定的形参槽位，便于后续按需求默认值
        val bound = BooleanArray(paramCount)
        // 1. 位置实参
        for (i in 0 until posCount) {
            declareParam(params[i], argValues[i], ctx)
            bound[i] = true
        }
        // 2. 命名实参
        if (namedValues.isNotEmpty()) {
            // 建立 name → index 索引；保留首个出现位置（params 内重名编译期已禁止，此处假定唯一）。
            val nameToIndex = LinkedHashMap<String, Int>(paramCount)
            for (i in 0 until paramCount) {
                nameToIndex[params[i]] = i
            }
            for (e in namedValues.entries) {
                val idx = nameToIndex[e.key]
                if (idx == null) {
                    throw DslRuntimeError("$hint 未知命名实参: ${e.key}")
                }
                if (bound[idx]) {
                    throw DslRuntimeError("$hint 命名实参与位置实参冲突: ${e.key}")
                }
                declareParam(params[idx], e.value, ctx)
                bound[idx] = true
            }
        }
        // 3. 未绑定形参取默认值
        for (i in 0 until paramCount) {
            if (bound[i]) continue
            val def = if (defaults.isEmpty()) null else defaults[i]
            if (def == null) {
                throw DslRuntimeError("$hint 缺少必需实参: ${params[i]}")
            }
            val value = ExprEvaluator.eval(def, ctx)
            declareParam(params[i], value, ctx)
        }
    }

    /** 写入单个形参；遵循 M5-a `_` 丢弃形参约定。 */
    private fun declareParam(name: String, value: Any?, ctx: EvalContext) {
        if ("_" != name) {
            ctx.declareVar(name, value)
        }
    }
}
